// Global mount point for the ContactLead popup, opened via Redux dispatch.
//
// Mirrors the <CreateLeadDialogWrapper /> singleton pattern: mounted once in
// the dashboard layout, listens to a Redux flag, and renders the modal when
// open. The wrinkle here is the V1/V2 swap — we reuse <ContactLeadPopupRouter>
// rather than re-implementing the gate, so this singleton automatically stays
// in sync with however the router decides V1 vs V2.
//
// Why this exists: non-React launchers (commandRegistry.js, future global
// keyboard shortcuts, deep-link handlers, etc.) can't mount a React component
// inline — they can only dispatch. Without this singleton, every non-React
// launcher would either need its own mount-point + V1/V2 swap, or skip the
// popup entirely (the gap the RCA called out).
//
// Usage from anywhere (React or not):
//     import { dispatch } from 'src/redux/store';
//     import { openContactLeadPopup } from 'src/redux/slices/contactLeadPopup';
//     dispatch(openContactLeadPopup({ leadId: 1234 }));
//
// The singleton fetches the lead via followUpSlice (slim payload — same
// endpoint the existing follow-up flows use) and feeds it to the router as
// `leadDetail`. Closing dispatches closeContactLeadPopup and clears
// state.lead.leadDetail to match the existing teardown convention.

import { useEffect } from 'react';
import { useSelector, dispatch } from '../redux/store';
import { closeContactLeadPopup } from '../redux/slices/contactLeadPopup';
import { followUpSlice, leadActions } from '../redux/slices/lead';
import ContactLeadPopupRouter from './fullScreenPopUps/ContactLeadPopupRouter';

export default function ContactLeadPopupSingleton() {
    const open = useSelector((state) => state.contactLeadPopup.open);
    const leadId = useSelector((state) => state.contactLeadPopup.leadId);
    const opts = useSelector((state) => state.contactLeadPopup.opts);
    const leadDetail = useSelector((state) => state.lead?.leadDetail);
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);

    // Load the lead when the singleton is opened with a leadId.
    // Skipped if leadDetail is already loaded for the same id (e.g. opened
    // from a lead-detail page where the slice already has the row).
    useEffect(() => {
        if (!open || !leadId || !practiceId) return;
        if (leadDetail?.id === leadId) return;
        dispatch(followUpSlice(leadId, practiceId, 'LeadDetail'));
    }, [open, leadId, practiceId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleClose = () => {
        dispatch(closeContactLeadPopup());
        // Match the teardown other follow-up flows do — clearing leadDetail
        // prevents a stale row flashing the next time the popup opens with a
        // different lead.
        if (leadActions?.clearLeadDetail) {
            dispatch(leadActions.clearLeadDetail());
        }
    };

    if (!open) return null;
    // Wait for the fetch to land before mounting — avoids the V1 popup
    // crashing on a null `user`. ContactLeadPopupRouter returns null on
    // !open so this is the equivalent guard for "waiting on data".
    if (!leadDetail || leadDetail.id !== leadId) return null;

    return (
        <ContactLeadPopupRouter
            open={open}
            onClose={handleClose}
            leadDetail={leadDetail}
            user={leadDetail}
            practiceId={practiceId}
            // Default superadmin-debug source; any dispatcher of
            // openContactLeadPopup({ opts: { debugSource } }) can override it
            // via the spread below.
            debugSource="singleton:redux-openContactLeadPopup"
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...opts}
        />
    );
}
